import { Box, styled, Typography, Link } from '@mui/material'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import Highlighter from 'react-highlight-words'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

const CustomWidthTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
})

const RiskMatrixEvaluatedData = ({ data, operator, rawText = '', attribute = '', dataResult = null }) => {
  const componentValueRef = useRef(null)
  const [isOverflowText, setIsOverflowText] = useState(false)

  const dataIsObject = useMemo(() => typeof data === 'object' && data !== null, [data])

  // Check if this is kyc_status or shareholders_kyc_status with person info
  const hasPersonLinks = useMemo(() => {
    if (!dataResult) return false
    if (attribute === 'kyc_status' && dataResult?.personId) return true
    if (attribute === 'shareholders_kyc_status' && Array.isArray(dataResult) && dataResult.length > 0) return true
    return false
  }, [attribute, dataResult])

  const stringifyObject = (data) => {
    if (Array.isArray(data)) {
      return data
        .map((item) => {
          return stringifyObject(item)
        })
        .join(', ')
    }
    if (typeof data === 'object') {
      return `${JSON.stringify(data)}`
    }
    return `${data}`
  }

  const text = useMemo(() => {
    return stringifyObject(data)
  }, [data])

  const highlights = useMemo(() => {
    if (operator === 'any_includes_any' && Array.isArray(rawText)) {
      return rawText
    } else if (operator === 'any_includes' && typeof value === 'string') {
      return [rawText]
    }

    return []
  }, [operator, rawText])

  const tooltipText = useMemo(() => {
    if (!isOverflowText || dataIsObject) {
      return ''
    }

    if (Array.isArray(data)) {
      return (
        <Box>
          {data.map((t) => (
            <Typography key={t}>
              - <Highlighter searchWords={highlights} textToHighlight={`${t}`} />
            </Typography>
          ))}
        </Box>
      )
    }
    return data
  }, [data, dataIsObject, highlights, isOverflowText])

  useEffect(() => {
    if (componentValueRef.current) {
      const { offsetHeight, scrollHeight, offsetWidth, scrollWidth } = componentValueRef.current
      setIsOverflowText(offsetHeight < scrollHeight || offsetWidth < scrollWidth)
    }
  }, [text])

  // Render with links if applicable
  if (hasPersonLinks) {
    if (attribute === 'kyc_status' && dataResult?.personId) {
      // Single person link for kyc_status
      return (
        <Link component={RouterLink} to={`/kyc/person/${dataResult.personId}`} target="_blank" rel="noopener">
          {dataResult.status}
        </Link>
      )
    }

    if (attribute === 'shareholders_kyc_status' && Array.isArray(dataResult)) {
      // Multiple person links for shareholders_kyc_status
      return (
        <CustomWidthTooltip
          title={
            <Box>
              {dataResult.map((person, idx) => (
                <Box key={person.personId || idx} sx={{ mb: 0.5 }}>
                  {person.personId ? (
                    <Link
                      component={RouterLink}
                      to={`/kyc/person/${person.personId}`}
                      target="_blank"
                      rel="noopener"
                      sx={{ color: 'white', textDecoration: 'underline' }}
                    >
                      {person.name}: {person.status}
                    </Link>
                  ) : (
                    <span style={{ color: 'white' }}>
                      {person.name}: {person.status}
                    </span>
                  )}
                </Box>
              ))}
            </Box>
          }
        >
          <span
            ref={componentValueRef}
            style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
          >
            {text}
          </span>
        </CustomWidthTooltip>
      )
    }
  }

  return (
    <CustomWidthTooltip title={tooltipText}>
      <span ref={componentValueRef} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {text}
      </span>
    </CustomWidthTooltip>
  )
}

export default RiskMatrixEvaluatedData
